



























































































































































































































import Vue from 'vue';
import { startCase } from 'lodash';
import WbTextField from '@/components/text_field/index.vue';
import WbSelect from '@/components/select/index.vue';
import initCheckout from './dependencies/init';
import completeCheckout, { CheckoutPaymentRequestBody } from './dependencies/complete_checkout';
import WbTextFieldLoader from '@/components/skeleton_loaders/text_field.vue';
import WbParagraphLoader from '@/components/skeleton_loaders/paragraph.vue';
import Disclosure from '@/views/student/components/disclosure/index.vue';
import statesByCountry from '@/helpers/regions';

export default Vue.extend({
  name: 'AutomaticPaymentSetup',

  components: {
    WbTextField,
    WbSelect,
    WbTextFieldLoader,
    WbParagraphLoader,
    Disclosure,
  },

  props: {
    projectData: {
      type: Object,
    },
  },

  data() {
    return {
      productId: '',
      product: {
        name: '',
        priceInCents: 0,
        currencyCode: '',
      },
      logoUrl: '',
      termsOfServiceUrl: '',
      projectName: '',
      paymentPlans: [] as { id: string; installments: number; downPaymentPercentage: number }[],
      state: 'loaded',
      paymentState: '',
      error: '',
      invalid: true,
      env: process.env.VUE_APP_BLUESNAP_ENV === 'production'
        ? 'https://ws.bluesnap.com'
        : 'https://sandbox.bluesnap.com',
      form: {
        card: {
          nameOnCard: '',
          token: '',
        },
        student: {
          firstName: '',
          lastName: '',
          email: '',
        },
        address: {
          country: '',
          postalCode: '',
        },
        selectedPaymentPlanId: null,
        hasAgreedToConditions: false,
      },
      errors: {
        nameOnCard: '',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
      },
      formInputsState: 'loading',
      contries: [
        { code: 'us', name: 'United States' },
        { code: 'ca', name: 'Canada' },
      ],
    };
  },

  computed: {
    states(): Record<any, any>[] {
      return statesByCountry[this.form.address.country] || [];
    },
  },

  async mounted() {
    this.productId = this.$route.params.productId as string;

    if (!this.productId) {
      this.$router.replace({
        path: `/student/checkout-full/${this.productId}/404`,
      });
      return;
    }

    try {
      this.formInputsState = 'loading';
      const init = await initCheckout(this.productId);
      this.form.card.token = init.cardToken;
      this.product.name = init.product.name;
      this.product.priceInCents = init.product.priceInCents;
      this.product.currencyCode = init.product.currencyCode;
      this.projectName = init.product.projectName;
      this.termsOfServiceUrl = init.product.termsOfServiceUrl || '';

      this.paymentPlans = init.paymentPlans;
      this.logoUrl = init.logoUrl;

      const obj = this.buildBluesnap(this.form.card.token);
      window.bluesnap.hostedPaymentFieldsCreate(obj);

      // On bluesnap build success, state is set to loaded.
      // if more than 10s goes by, we assume it failed.
      setTimeout(() => {
        if (this.formInputsState === 'loading') {
          this.$emit('setErrorMessage', `
            We had trouble loading the credit card form. Try and refresh your browser.
            If this persists, please reach out to us.
          `);
        }
      }, 10000);
    } catch (error) {
      this.handleInitError(error);
    }
  },

  methods: {
    handleInitError(error) {
      if (
        error?.status === 404
        || error?.data?.type === 'NoProductFound'
        || error?.data?.type === 'CheckoutNotAvailable'
      ) {
        this.$router.replace({ path: '404' });
        return;
      }

      this.$emit('setErrorMessage', `
        We had trouble loading the credit card form. Try and refresh your browser.
        If this persists, please reach out to us.
      `);
    },

    async submit() {
      this.error = '';

      if (!this.form.card.nameOnCard) {
        this.errors.nameOnCard = 'Field Required';
        return;
      }

      window.bluesnap.hostedPaymentFieldsSubmitData(
        async (callback) => {
          if (callback.error != null) {
            this.error = callback.error;
            return;
          }

          const { cardData } = callback as {
            cardData: {
              token: string;
              cardSubType: string;
              last4Digits: string;
              exp: string;
            };
          };

          const form: CheckoutPaymentRequestBody = {
            student: {
              firstName: this.form.student.firstName,
              lastName: this.form.student.lastName,
              email: this.form.student.email,
            },
            paymentData: {
              card: {
                token: this.form.card.token,
                type: cardData.cardSubType,
                last4Digits: cardData.last4Digits,
                cardExpiry: cardData.exp,
              },
              billingAddress: this.form.address,
            },
            selectedPaymentPlanId: this.form.selectedPaymentPlanId,
          };

          await this.submitCheckout(form);
        },
      );
    },

    onChangeName() {
      if (!this.form.card.nameOnCard) {
        this.errors.nameOnCard = 'Field Required';
      } else {
        this.errors.nameOnCard = '';
      }
    },

    async submitCheckout(requestBody: CheckoutPaymentRequestBody) {
      if (this.paymentState === 'loading') {
        return;
      }

      try {
        this.paymentState = 'loading';
        const { productId } = this.$route.params;
        const completionDetails = await completeCheckout({ productId, requestBody });

        this.$router.replace({ query: { checkoutSessionId: completionDetails.checkoutSessionId } });

        if (completionDetails.redirectPage) {
          this.$router.push({
            path: 'completed-redirect',
            query: {
              redirectPage: completionDetails.redirectPage,
              firstName: completionDetails.studentFirstName,
              lastName: completionDetails.studentLastName,
              productName: completionDetails.productName,
              email: completionDetails.email,
              price: `${completionDetails.price}`,
            },
          });
        } else {
          this.$router.push({
            path: 'completed',
            query: { ...this.$route.query },
          });
        }
      } catch (error) {
        this.handleErrorCheckout(error);
      } finally {
        this.paymentState = 'loaded';
      }
    },

    handleErrorCheckout(error) {
      const defaultMessage = `Sorry, we could not complete your checkout at this time.
        Please try another payment method or please try again later. Thank you!`;

      if (!error?.type || error?.status === 500 || error?.type === 'GenericError') {
        this.error = defaultMessage;
        return;
      }
      this.error = error?.message;
    },

    disclosureChange(agreed: boolean) {
      this.form.hasAgreedToConditions = agreed;
    },

    setErrorMessage(error: string) {
      this.$emit('setErrorMessage', error);
    },

    buildBluesnap(sessiontoken: string) {
      return {
        // insert your Hosted Payment Fields token
        token: sessiontoken,
        onFieldEventHandler: {
          setupComplete: () => {
            this.formInputsState = 'loaded';
          },

          onFocus(tagId) {
            // Handle focus
            // changeImpactedElement(tagId, 'hosted-field-valid hosted-field-invalid', 'hosted-field-focus');
          },

          onBlur(tagId) {
            // Handle blur
            // changeImpactedElement(tagId, 'hosted-field-focus');
          },

          onError: (tagId: string, errorCode: string, errorDescription: string) => {
            switch (tagId) {
            case 'ccn':
              this.errors.cardNumber = startCase(errorDescription);
              break;
            case 'exp':
              this.errors.expiryDate = startCase(errorDescription);
              break;
            case 'cvv':
              this.errors.cvv = startCase(errorDescription);
              break;
            default:
              break;
            }
          },

          onType(tagId, cardType, cardData) {
            // get card type from cardType and display card image
          },

          onValid: (tagId) => {
            switch (tagId) {
            case 'ccn':
              this.errors.cardNumber = '';
              break;
            case 'exp':
              this.errors.expiryDate = '';
              break;
            case 'cvv':
              this.errors.cvv = '';
              break;
            default:
              break;
            }
          },
        },
        // styling is optional
        style: {
          ':focus': {
            color: '#40496b',
          },
          input: {
            color: '#40496b',
            'font-size': '0.875rem',
          },
        },
        ccnPlaceHolder: '4111 2222 3333 4444',
        cvvPlaceHolder: '123',
        expPlaceHolder: 'MM / YY',
      };
    },
  },
});
